import React from "react";
import { useNavigate } from "react-router-dom";

export default function HypPrivacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate("/")} className="flex items-center">
            <h1 className="text-2xl font-bold" style={{ color: '#30853A' }}>Plexys</h1>
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-3 py-1 rounded-full">
            iOS App
          </span>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">HYP Privacy Policy</h1>
        <p className="text-lg text-gray-500 mb-2">Daily Hypnosis — iOS App</p>
        <p className="text-sm text-gray-500 mb-8">
          Effective Date: January 24, 2026 · Last Updated: February 11, 2026
        </p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700">
              HYP ("we," "our," or "the app") is a hypnosis and mindfulness app designed to help users 
              build positive habits and break unwanted behaviors through guided audio sessions. 
              This Privacy Policy explains how we collect, use, and protect your information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Information You Provide</h3>
            <p className="text-gray-700 mb-3">When you use HYP, you may provide:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Profile Information:</strong> Your name, stored locally on your device only</li>
              <li><strong>Behavior Selections:</strong> Which behavior programs you choose to follow</li>
              <li><strong>Session History:</strong> Records of completed hypnosis and mindfulness sessions</li>
              <li><strong>Streak Data:</strong> Daily usage streaks and progress tracking</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Storage</h2>
            
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-6 mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🔒 Local Storage Only</h3>
              <p className="text-gray-700 mb-3"><strong>All your data is stored locally on your device.</strong> HYP does not:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Upload your data to cloud servers</li>
                <li>Require user accounts or sign-up</li>
                <li>Collect email addresses or personal identifiers</li>
                <li>Share your data with third parties</li>
                <li>Use analytics or tracking frameworks</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 mb-3">We use your information solely to:</p>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Deliver Sessions:</strong> Play the appropriate pre-recorded hypnosis audio for your selected behavior program</li>
              <li><strong>Track Progress:</strong> Display your session history, streaks, and program stage</li>
              <li><strong>Send Reminders:</strong> With your permission, send local notifications to remind you of daily sessions</li>
              <li><strong>Remember Preferences:</strong> Store your selected behaviors and session progress</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Purchases</h2>
            <p className="text-gray-700">
              In-app purchases and subscriptions are processed securely by Apple through the App Store. 
              We do not have access to your payment information, credit card details, or Apple ID. 
              All purchase transactions are handled entirely by Apple's payment infrastructure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Sharing</h2>
            <p className="text-gray-700">
              We do not sell, rent, or share your personal information with any third parties. 
              No data leaves your device.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 mb-3">We implement appropriate measures to protect your information:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>All data remains on your device</li>
              <li>No network transmission of personal data</li>
              <li>Standard iOS data protection and encryption</li>
              <li>No external servers or databases</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700">
              HYP is not intended for use by children under 13. We do not knowingly collect 
              information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 mb-3">You have complete control over your data:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access:</strong> All your data is visible within the app</li>
              <li><strong>Delete:</strong> Remove all app data by deleting the app from your device</li>
              <li><strong>Notifications:</strong> Manage notification permissions in iOS Settings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. Changes will be reflected by updating 
              the "Last Updated" date at the top of this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-3">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> support@nsgcode.com<br />
              <strong>Website:</strong>{" "}
              <a href="https://www.plexys.co" className="text-purple-700 hover:underline">
                https://www.plexys.co
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>This privacy policy is provided in compliance with applicable privacy laws and Apple App Store requirements.</p>
          <p className="mt-2">© {new Date().getFullYear()} HYP. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
